import { useState, useRef } from 'react';
import * as THREE from 'three';
import type { 
  WorkspaceState, 
  WorkspaceCallbacks, 
  PlaneInfo, 
  QuickNodeCoords,
  TooltipPosition,
  SelectionBounds,
  MovementPlane
} from '../../utils/workspace.types';
import {
  DEFAULT_PLANE_INFO,
  DEFAULT_QUICK_NODE_COORDS,
  DEFAULT_TOOLTIP_POSITION
} from '../../utils/workspace.constants';

/**
 * Central state management hook for STAADWorkspace3D
 * Manages all local component state while preserving exact behavior
 */
export const useWorkspaceState = () => {
  // Mode states
  const [isNodeCreationMode, setIsNodeCreationMode] = useState(false);
  const [isBeamCreationMode, setIsBeamCreationMode] = useState(false);
  const [isGridVisible, setIsGridVisible] = useState(false); // Grid hidden by default
  // enable zoom-to-cursor by default per new requirements
  const [zoomToCursor, setZoomToCursor] = useState(true);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [showQuickNodeDialog, setShowQuickNodeDialog] = useState(false);
  const [quickNodeCoords, setQuickNodeCoords] = useState<QuickNodeCoords>(DEFAULT_QUICK_NODE_COORDS);
  const [editableCoords, setEditableCoords] = useState<QuickNodeCoords>(DEFAULT_QUICK_NODE_COORDS);
  const [lastCreatedNodeId, setLastCreatedNodeId] = useState<string | null>(null);
  const [showNodeLabels, setShowNodeLabels] = useState(true);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [planeInfo, setPlaneInfo] = useState<PlaneInfo>(DEFAULT_PLANE_INFO);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectionFilter, setSelectionFilter] = useState<'all' | 'nodes' | 'beams'>('all');
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  const [selectionStart, setSelectionStart] = useState<SelectionBounds | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<SelectionBounds | null>(null);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  const [showAnalysisPanel, setShowAnalysisPanel] = useState(false);

  // Professional drag state management
  const [isDragging, setIsDragging] = useState(false);
  const [draggedNodeId, setDraggedNodeId] = useState<string | null>(null);
  const [dragStartPosition, setDragStartPosition] = useState<THREE.Vector3 | null>(null);
  const [currentDragPosition, setCurrentDragPosition] = useState<THREE.Vector3 | null>(null);
  const [dragPlane, setDragPlane] = useState<MovementPlane>('XY');

  // Coordinate input state
  // Coordinate input state (removed - precise input modal deprecated)
  // NOTE: showCoordinateInput, selectedNodeForInput and coordinateInputValues removed
  const [isAnimatingToPosition, setIsAnimatingToPosition] = useState(false);

  // Coordinate tooltip state
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>(DEFAULT_TOOLTIP_POSITION);

  // Quick node offset options
  const [quickNodeOffsetEnabled, setQuickNodeOffsetEnabled] = useState<boolean>(false);
  const [quickNodeOffsetCount, setQuickNodeOffsetCount] = useState<number>(1);
  const [quickNodeOffsetValues, setQuickNodeOffsetValues] = useState<QuickNodeCoords>(DEFAULT_QUICK_NODE_COORDS);

  // Performance optimization refs
  const isDraggingRef = useRef(false);
  const dragUpdateRef = useRef<NodeJS.Timeout>();
  const orbitControlsRef = useRef<any>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Compose state object
  const state: WorkspaceState = {
    isNodeCreationMode,
    isBeamCreationMode,
    isGridVisible,
    zoomToCursor,
    selectedNodeId,
    showQuickNodeDialog,
    quickNodeCoords,
    editableCoords,
    lastCreatedNodeId,
    showNodeLabels,
    isShiftPressed,
    planeInfo,
    // orbitControlsRef,
    isSelectionMode,
    selectionFilter,
    isDraggingSelection,
    selectionStart,
    selectionEnd,
    isCtrlPressed,
    showAnalysisPanel,
    isDragging,
    draggedNodeId,
    dragStartPosition,
    currentDragPosition,
    dragPlane,
  // precise input fields removed
  isAnimatingToPosition,
  quickNodeOffsetEnabled,
  quickNodeOffsetCount,
  quickNodeOffsetValues,
    hoveredNodeId,
    tooltipPosition
  };

  // Compose callbacks object
  const callbacks: WorkspaceCallbacks = {
    setIsNodeCreationMode,
    setIsBeamCreationMode,
    setIsGridVisible,
    setZoomToCursor,
    setSelectedNodeId,
    setShowQuickNodeDialog,
    setQuickNodeCoords,
    setEditableCoords,
    setLastCreatedNodeId,
    setShowNodeLabels,
    setIsShiftPressed,
    setPlaneInfo,
    setIsSelectionMode,
    setSelectionFilter,
    setIsDraggingSelection,
    setSelectionStart,
    setSelectionEnd,
    setIsCtrlPressed,
    setShowAnalysisPanel,
    setIsDragging,
    setDraggedNodeId,
    setDragStartPosition,
    setCurrentDragPosition,
    setDragPlane,
  // precise input setters removed
    setIsAnimatingToPosition,
  setQuickNodeOffsetEnabled,
  setQuickNodeOffsetCount,
  setQuickNodeOffsetValues,
    setHoveredNodeId,
    setTooltipPosition
  };

  // Compose refs object
  const refs = {
    isDraggingRef,
    dragUpdateRef,
    orbitControlsRef,
    canvasRef
  };

  return {
    state,
    callbacks,
    refs
  };
};
